/**
 * serviceClaims.ts — the standing claims the journey makes about the SERVICE,
 * each written once, for every screen that prints it.
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: journey look-and-feel,
 * loaders (2026-09-25).
 *
 * WHY A MODULE. The journey's first-load loader shows claims about the service
 * while it waits (`ui/BrandedLoader.tsx`, the journey look), and a claim
 * shown there must be one the site ALREADY makes — never a new regulatory,
 * timing or outcome claim (DMCC Act 2024 / CAP Code 3.1 and section 13). The
 * surest way to keep "already makes" true is for the loader and the screen
 * that makes the claim to print the SAME constant: reword it here and both
 * move together, and nothing can leave the loader saying something the rest
 * of the journey no longer says.
 *
 * All of them are shown BEFORE any eligibility decision (Step 0, the "What
 * happens next" screen, and the entry loader), so they stay category-level
 * (R28), say nothing about the visitor's body (Art 9) and quote no price.
 *
 * 2026-09-25 (CTO: "use that loading page as a bit of a small content page";
 * and that evening, "load one after the other, showing the journey"): the
 * entry loader's one quiet reassurance line became a small card that shows the
 * journey, revealed one item after another (`entryLoaderContent.ts`). Its steps
 * and its chips are built from these constants and from the ones the other
 * screens already print — never a claim of its own.
 *
 * SHORT FORMS (2026-09-25, later — CTO: "it's too much text … use very catchy
 * text … grab the attention because we have a very short window"). The card
 * now leads each step with a 2–5 word headline and at most one short line. A
 * short form lives HERE, directly under the long claim it restates, with a
 * comment naming every screen that makes that claim in full — so the short
 * line can never say more than the long one, and a reword of the long one
 * shows the short one sitting beside it. `entryLoaderContent.spec` pins each
 * short form against its source (the source still says it, word for word or
 * word by word).
 */
import { CHECK_MINUTES } from './progressCopy';

/**
 * How long the check takes — Step 0's help ("… — a 2-minute check, reviewed by a
 * UK clinician.") and, verbatim, the entry loader card's first headline.
 *
 * ONE NUMBER (review, 2026-09-25). The figure is `CHECK_MINUTES`
 * (`progressCopy.ts`), the number the "Leave this step?" dialog quotes too ("The
 * whole check only takes about 2 minutes to finish.", #460) — so Step 0, the
 * entry loader card and the dialog can never promise two different lengths for
 * the one check. Change the number there and all three move; change the words
 * here. (`progressCopy.ts` imports nothing, so there is no cycle.)
 */
export const TWO_MINUTE_CHECK = `${CHECK_MINUTES}-minute check`;

/**
 * Who wrote the questions and who reads the answers — the first sentence of the
 * first "What happens next" step. (The entry loader card's first cut joined it
 * to the 2-minute check; the icon-led card of 2026-09-25 no longer shows it.)
 */
export const QUESTIONS_WRITTEN_BY_CLINICIANS = 'Written by our clinicians, and read by one.';

/** The third "What happens next" step's title — the clinician review every submitted assessment is routed to. */
export const CLINICIAN_REVIEWS_ASSESSMENT = 'A UK clinician reviews your assessment';

/**
 * SHORT FORM of {@link CLINICIAN_REVIEWS_ASSESSMENT} — the entry loader card's
 * third headline. The same claim as the long title (also the celebration's hold
 * trust point, `checkout/celebrationCopy.ts`, and the confirmation's first step,
 * `checkout/paymentCopy.ts`), and the Result screen's own words for the step
 * ("Clinician review", `resultCopy.ts` RESULT_JOURNEY_STEPS and its "What
 * happens next" card).
 */
export const CLINICIAN_REVIEW_SHORT = 'UK clinician review';

/**
 * How long the review usually takes — the ONLY timing claim the site makes about
 * it, run on from the review step's headline on "What happens next" since
 * 2026-09-26 (`WhatHappensNext.tsx`, lower-cased to continue the sentence), and
 * printed as the first sentence of the hold route's confirmation step
 * (`checkout/paymentCopy.ts`) and the celebration's hold trust point
 * (`checkout/celebrationCopy.ts`) — "Usually within one working day." The entry
 * loader card's third line (2026-09-25). "Usually" stays: it is the qualifier
 * the claim is made with, and dropping it would make a promise.
 */
export const REVIEW_USUALLY_WITHIN_ONE_WORKING_DAY = 'Usually within one working day';

/**
 * The "What happens next" screen's line under its heading and Step 0's help (its
 * second sentence). The entry loader card says it in its short form, below.
 */
export const NO_CHARGE_UNTIL_APPROVED = 'No charge until your treatment is approved.';

/**
 * SHORT FORM of {@link NO_CHARGE_UNTIL_APPROVED} — the entry loader card's
 * second headline (2026-09-25). The same claim, with "your treatment" left
 * implied; the Result screen makes it too ("Your card is only charged once your
 * treatment is approved — never before.", `ResultScreen.tsx`). ⚠️ Never "£0
 * today" or "free": a pre-authorisation HOLD is placed on the card at checkout
 * (the "What happens next" step 2 body), so the honest claim is about the
 * CHARGE, and only this one.
 */
export const NO_CHARGE_UNTIL_APPROVED_SHORT = 'No charge until approved';
